import React, { useState, useEffect } from 'react';
import { updateConfiguracionPago, ConfiguracionPago } from '../../services/configuracionPagoService';

interface EditConfigPagosProps {
    isOpen: boolean;
    onClose: () => void;
    onConfiguracionUpdated: (configuracion: ConfiguracionPago) => void;
    configuracion: ConfiguracionPago | null;
}

const EditConfigPagos: React.FC<EditConfigPagosProps> = ({
    isOpen,
    onClose,
    onConfiguracionUpdated,
    configuracion
}) => {
    const [nombre, setNombre] = useState('');
    const [tipo, setTipo] = useState<'efectivo' | 'banco' | 'otro'>('efectivo');
    const [afectaCaja, setAfectaCaja] = useState<boolean>(false);
    const [requiereCambio, setRequiereCambio] = useState<boolean>(false);
    const [permiteMontoPersonalizado, setPermiteMontoPersonalizado] = useState<boolean>(false);
    const [activo, setActivo] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (configuracion) {
            setNombre(configuracion.nombre);
            setTipo(configuracion.tipo);
            setAfectaCaja(configuracion.afecta_caja);
            setRequiereCambio(configuracion.requiere_cambio);
            setPermiteMontoPersonalizado(configuracion.permite_monto_personalizado);
            setActivo(configuracion.activo);
        }
    }, [configuracion]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            if (!configuracion) return;

            if (!nombre.trim()) {
                throw new Error('El nombre es obligatorio');
            }

            const configuracionActualizada = await updateConfiguracionPago(configuracion.id, {
                nombre: nombre.trim(),
                tipo,
                afecta_caja: afectaCaja,
                requiere_cambio: requiereCambio,
                permite_monto_personalizado: permiteMontoPersonalizado,
                activo
            });

            onConfiguracionUpdated(configuracionActualizada);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al actualizar la configuración de pago');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen || !configuracion) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-md mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Editar Configuración de Pago</h3>
                    <div className="flex items-center gap-4">
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nombre *
                        </label>
                        <input
                            type="text"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Ej: Efectivo con Cambio"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tipo *
                        </label>
                        <select
                            value={tipo}
                            onChange={(e) => setTipo(e.target.value as 'efectivo' | 'banco' | 'otro')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                            disabled={isSubmitting}
                        >
                            <option value="efectivo">Efectivo</option>
                            <option value="banco">Banco</option>
                            <option value="otro">Otro</option>
                        </select>
                    </div>

                    <div className="space-y-3 mb-4">
                        {/* Afecta a caja */}
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">Afecta a caja</span>
                                <p className="text-xs text-gray-500 mt-1">{afectaCaja ? 'Sí' : 'No'}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={afectaCaja}
                                    onChange={() => setAfectaCaja(!afectaCaja)}
                                    disabled={isSubmitting}
                                />
                                <div className={`w-10 h-6 rounded-full transition-colors ${afectaCaja ? 'bg-[#c1348b]' : 'bg-gray-400'}`}>
                                    <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${afectaCaja ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                        </div>

                        {/* Requiere cambio */}
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">Requiere cambio</span>
                                <p className="text-xs text-gray-500 mt-1">{requiereCambio ? 'Sí' : 'No'}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={requiereCambio}
                                    onChange={() => setRequiereCambio(!requiereCambio)}
                                    disabled={isSubmitting}
                                />
                                <div className={`w-10 h-6 rounded-full transition-colors ${requiereCambio ? 'bg-[#c1348b]' : 'bg-gray-400'}`}>
                                    <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${requiereCambio ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                        </div>

                        {/* Permite monto personalizado (NUEVO) */}
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">Permite monto personalizado</span>
                                <p className="text-xs text-gray-500 mt-1">{permiteMontoPersonalizado ? 'Sí' : 'No'}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={permiteMontoPersonalizado}
                                    onChange={() => setPermiteMontoPersonalizado(!permiteMontoPersonalizado)}
                                    disabled={isSubmitting}
                                />
                                <div className={`w-10 h-6 rounded-full transition-colors ${permiteMontoPersonalizado ? 'bg-[#c1348b]' : 'bg-gray-400'}`}>
                                    <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${permiteMontoPersonalizado ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                        </div>

                        {/* Estado */}
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">Estado de la Configuración</span>
                                <p className="text-xs text-gray-500 mt-1">{activo ? 'Activo' : 'Inactivo'}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={activo}
                                    onChange={() => setActivo(!activo)}
                                    disabled={isSubmitting}
                                />
                                <div className={`w-10 h-6 rounded-full transition-colors ${activo ? 'bg-[#c1348b]' : 'bg-gray-400'}`}>
                                    <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${activo ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                'Actualizar Configuración'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditConfigPagos;